/**
 * Services and Names. Services: what the machines offer on the
 * mesh and who may reach each one by the grants (and the ports the policy names that nothing
 * offers: "Discovered"). Names: every machine's name on the mesh and its suffix.
 */

import type { MeshGrantRef, MeshNode, MeshServiceView } from '@aioe/shared';
import { Globe } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { MachineActions, type Action } from './meshMachines.tsx';
import { Empty, ErrorNote, ListBlock, ListGroup, StatusMark, type ArtTone } from './ui.tsx';

const NOT_OFFERED = 'Exit nodes, subnet routers, public exposure (Funnel), DNS filtering, mesh SSH and file drops are not offered, by design: the mesh joins your organisation’s own machines, and nothing on it is routed to or from the internet. Remote control and the node host link replace shell access.';

const grantWords = (g: MeshGrantRef) => (g.kind === 'owners' ? 'Owners reach their own nodes' : g.kind === 'operators' ? 'Operators reach everything' : g.label);

function who(s: MeshServiceView): string {
  const r = s.reachableBy;
  if (r.nodes === 0 && s.assigned === 0) return 'Nobody yet';
  const names = r.people.map((p) => p.name ?? p.id);
  const parts = [...(names.length > 3 ? [`${names.slice(0, 3).join(', ')} and ${names.length - 3} more`] : names.length ? [names.join(', ')] : []), ...r.tags.map((t) => t), ...(r.people.length === 0 && r.tags.length === 0 ? [`${r.nodes} ${r.nodes === 1 ? 'machine' : 'machines'}`] : [])];
  return parts.join(' · ');
}

export function MeshServices({ tone: _tone }: { tone: ArtTone }) {
  const data = useLoad(() => routes.meshServices(), [], 60_000);
  const rows = data.data?.services ?? [];
  const named = data.data?.named ?? [];
  return (
    <div className="grid gap-4">
      <ErrorNote error={data.error} />
      <ListGroup title="Services" count={data.data ? rows.length : undefined} description="What the machines offer on the mesh, and who may reach each one."><ListBlock flush>
        {rows.length === 0 && !data.loading ? (
          <p className="px-4 py-3 text-sm text-muted">No machine offers a service yet. A mesh agent started with --expose offers a port to the machines the policy allows.</p>
        ) : (
          <table className="w-full table-fixed text-sm">
            <thead className="text-left text-xs text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Service</th>
                <th className="hidden px-4 py-2 font-medium md:table-cell">Who may reach it</th>
                <th className="hidden px-4 py-2 font-medium lg:table-cell">Because of</th>
                <th className="w-28 px-4 py-2 font-medium md:w-32">Machine</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={`${s.nodeId}:${s.port}`} className="border-t border-separator align-top">
                  <td className="px-4 py-2">
                    <span className="block truncate font-medium">
                      {s.name} <span className="mono tabular-nums text-muted">:{s.port}</span>
                    </span>
                    <span className="mono block truncate text-xs text-muted" title={s.dnsName}>
                      {s.nodeName}
                    </span>
                    <span className="block text-xs text-muted md:hidden">{who(s)}</span>
                  </td>
                  <td className="hidden px-4 py-2 text-xs md:table-cell">{who(s)}</td>
                  <td className="hidden px-4 py-2 text-xs text-muted lg:table-cell">{[...s.grants.map(grantWords), ...(s.assigned ? [`${s.assigned} by assignment`] : [])].join(' · ') || 'No grant opens this port'}</td>
                  <td className="px-4 py-2">{s.disabled ? <StatusMark state="disabled" label="Off" /> : <StatusMark state={s.online ? 'online' : 'offline'} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListBlock></ListGroup>
      <ListGroup title="Discovered" count={data.data ? named.length : undefined} description="Ports the access rules name that no machine offers yet."><ListBlock flush>
        {named.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted">Every port the rules name is offered by a machine.</p>
        ) : (
          <ul className="divide-y divide-separator text-sm" role="list">
            {named.map((n, i) => (
              <li key={i} className="flex flex-wrap items-baseline justify-between gap-x-4 px-4 py-2.5">
                <span>
                  <span className="mono tabular-nums">{n.port}</span> on <span className="mono">{n.selector}</span>
                </span>
                <span className="text-xs text-muted">
                  {grantWords(n.grant)} · {n.machines} {n.machines === 1 ? 'machine' : 'machines'} picked today
                </span>
              </li>
            ))}
          </ul>
        )}
      </ListBlock></ListGroup>
      <p className="px-1 text-xs text-muted">{NOT_OFFERED}</p>
    </div>
  );
}

export function MeshNames({ tone, nodes, isAdmin, onChanged }: { tone: ArtTone; nodes: readonly MeshNode[]; isAdmin: boolean; onChanged: () => void }) {
  const [action, setAction] = useState<Action>();
  const suffix = nodes.find((n) => n.dnsName)?.dnsName?.replace(/^[^.]+\./, '');
  if (nodes.length === 0) return <Empty tone={tone} icon={<Globe />} title="No machines yet" body="Each machine gets a name on the mesh when it joins." />;
  return (
    <div className="grid gap-4">
      <ListGroup title="Names" count={nodes.length} description={suffix ? `Every machine answers to its name under .${suffix}` : 'Every machine answers to a name on the mesh'}><ListBlock flush>
        <div className="border-b border-separator px-4 py-3 text-sm">
          <span className="text-muted">Mesh name suffix </span>
          <span className="mono">.{suffix ?? 'mesh.internal'}</span>
          <p className="mt-1 text-xs text-muted">Names resolve only inside the mesh, for the machines the policy lets reach each other. They are never published to public DNS. The suffix is fixed by the organisation, so a name cannot clash with one outside.</p>
        </div>
        <ul className="divide-y divide-separator text-sm" role="list">
          {nodes.map((n) => (
            <li key={n.id} className="flex items-center justify-between gap-3 px-4 py-2">
              <span className="min-w-0">
                <span className="block truncate font-medium">{n.name}</span>
                <span className="mono block truncate text-xs text-muted">{n.dnsName ?? n.address}</span>
              </span>
              {isAdmin && (
                <Button size="sm" variant="ghost" onPress={() => setAction({ kind: 'rename', node: n })}>
                  Rename
                </Button>
              )}
            </li>
          ))}
        </ul>
      </ListBlock></ListGroup>
      <MachineActions action={action} onClose={() => setAction(undefined)} onChanged={onChanged} onRemoved={() => undefined} />
    </div>
  );
}
